import {Menu, MenuItem} from 'platform/components';
import {Box, HStack, Show, Spinner} from 'platform/foundation';
import {css} from 'styled-components';

import {ReactNode, useEffect} from 'react';

import {isNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';

import {RequiredTestIdProps, suffixTestId, useQueryState} from 'shared/web';

import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {queryParams} from '../../utils/queryParams';
import {ControlledSection} from './types/ControlledSection';

export type SectionItem = {
  content: ReactNode;
} & MenuItem;

export type SectionsProps = {
  sections: SectionItem[];
  defaultSection?: string;
  activityPanel?: ReactNode;
  isLocked?: boolean;
  isFullHeight?: boolean;
} & RequiredTestIdProps &
  ControlledSection;

export function Sections(props: SectionsProps) {
  const [innerActiveSectionId, setInnerActiveSectionId] = useQueryState(
    queryParams.COMPONENT_SECTIONS_SECTION
  );

  const [canReadSalesActivities] = usePermissions({
    permissionKeys: ['readSalesActivities'],
  });

  const activeSectionId =
    props.activeSectionId ?? innerActiveSectionId ?? props.defaultSection ?? props.sections[0].id;

  const getSectionById = props.sections.find(({id}) => id === activeSectionId);

  useEffect(() => {
    if (props.activeSectionId === activeSectionId) {
      return;
    }

    if (isNil(props.activeSectionId)) {
      const isSelectedSection = props.sections.some((section) => section.id === activeSectionId);

      if (isSelectedSection) {
        return;
      }

      setInnerActiveSectionId(props.defaultSection ?? props.sections[0].id);
      return;
    }

    setInnerActiveSectionId(props.activeSectionId);
  }, [props.activeSectionId]);

  const setActiveSectionId = props.onSectionChange ?? setInnerActiveSectionId;

  const handleSectionChange = (sectionId: string) => {
    setActiveSectionId(sectionId);
  };

  const content = (
    <>
      {getSectionById?.content}
      <Show
        when={props.activityPanel && canReadSalesActivities}
        whenFeatureEnabled={featureFlags.CRM_SALES_ACTIVITIES}
      >
        {props.activityPanel}
      </Show>
    </>
  );

  return (
    <HStack height={props.isFullHeight ? '100%' : undefined}>
      <Show when={props.sections}>
        <Box minWidth={54} width={54}>
          {props.sections && (
            <Box paddingVertical={4} paddingLeft={4}>
              <Menu
                items={props.sections}
                activeItemId={activeSectionId}
                onItemChange={handleSectionChange}
                data-testid={suffixTestId('page-navigation', props)}
              />
            </Box>
          )}
        </Box>
      </Show>
      {props.isLocked ? (
        <div
          css={css`
            position: relative;
            height: 100%;
            flex: 1 1 0%;
            min-width: 0;
            display: flex;
            overflow: hidden;
          `}
        >
          <HStack spacing={4} width="100%" height={props.isFullHeight ? '100%' : undefined}>
            {content}
          </HStack>
          <div
            css={css`
              cursor: not-allowed;
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.1);
              z-index: 99999;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <Spinner />
          </div>
        </div>
      ) : (
        <HStack spacing={4} grow={1} minWidth={0}>
          {content}
        </HStack>
      )}
    </HStack>
  );
}
